import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { initWebVitals } from "@/lib/webVitals";
import { AuthProvider } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { Toaster } from "@/components/ui/sonner";
import { RequireStaff } from "@/components/Guards";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LOGO_URL } from "@/components/BobaLogo";

/* ---------- Public routes: eager-load Home (LCP), lazy-load rest ---------- */
import Home from "@/pages/public/Home";
import MenuBoard from "@/pages/public/MenuBoard";
const MenuPage = lazy(() => import("@/pages/public/Menu"));
const Rewards = lazy(() => import("@/pages/public/Rewards"));
const About = lazy(() => import("@/pages/public/About"));
const Contact = lazy(() => import("@/pages/public/Contact"));
const FAQ = lazy(() => import("@/pages/public/FAQ"));
const Events = lazy(() => import("@/pages/public/Events"));
const Order = lazy(() => import("@/pages/public/Order"));
const OrderCheckout = lazy(() => import("@/pages/public/Order").then(m => ({ default: m.CheckoutPage })));
const OrderConfirmation = lazy(() => import("@/pages/public/Order").then(m => ({ default: m.ConfirmationPage })));
const Login = lazy(() => import("@/pages/auth/Login"));
const StaffSetup = lazy(() => import("@/pages/auth/StaffSetup"));
const CustomerSignup = lazy(() => import("@/pages/auth/CustomerSignup"));

/* ---------- Admin bundle: lazy-load entire tree behind the auth wall ---------- */
const AdminShell = lazy(() => import("@/pages/admin/AdminShell"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const POS = lazy(() => import("@/pages/admin/POS"));
const OrdersAdmin = lazy(() => import("@/pages/admin/OrdersAdmin"));
const MenuAdmin = lazy(() => import("@/pages/admin/MenuAdmin"));
const MenuBoardEditor = lazy(() => import("@/pages/admin/MenuBoardEditor"));
const InventoryAdmin = lazy(() => import("@/pages/admin/InventoryAdmin"));
const CustomersAdmin = lazy(() => import("@/pages/admin/CustomersAdmin"));
const ReportsAdmin = lazy(() => import("@/pages/admin/ReportsAdmin"));
const SettingsAdmin = lazy(() => import("@/pages/admin/SettingsAdmin"));
const PaymentsAdmin = lazy(() => import("@/pages/admin/PaymentsAdmin"));
const UsersAdmin = lazy(() => import("@/pages/admin/UsersAdmin"));
const EventsAdmin = lazy(() => import("@/pages/admin/EventsAdminTabs"));
const LoyaltyAdmin = lazy(() => import("@/pages/admin/LoyaltyAdmin"));
const ShiftsAdmin = lazy(() => import("@/pages/admin/ShiftsAdmin"));
const GiftCardsAdmin = lazy(() => import("@/pages/admin/GiftCardsAdmin"));

/* ---------- Legal pages: T&C and Privacy ---------- */
const Terms = lazy(() => import("@/pages/public/Terms"));
const Privacy = lazy(() => import("@/pages/public/Privacy"));
const GiftCard = lazy(() => import("@/pages/public/GiftCard"));

/* ---------- Loader shown during chunk fetches (mobile-friendly, brand-flavored) ---------- */
function RouteLoader() {
  return (
    <div
      data-testid="route-loader"
      className="fixed inset-0 grid place-items-center bg-background/80 backdrop-blur-sm z-50"
      role="status"
      aria-label="Loading"
    >
      <div className="flex items-center gap-3">
        <span
          className="w-3 h-3 rounded-full animate-bounce"
          style={{ background: "#5B3FD1", animationDelay: "0ms" }}
        />
        <span
          className="w-3 h-3 rounded-full animate-bounce"
          style={{ background: "#ec4899", animationDelay: "150ms" }}
        />
        <span
          className="w-3 h-3 rounded-full animate-bounce"
          style={{ background: "#5B3FD1", animationDelay: "300ms" }}
        />
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    document.title = "The Boba Place";
    // Preload the brand logo so it's instant on Home + navbar.
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = LOGO_URL;
    link.fetchPriority = "high";
    document.head.appendChild(link);
    // Report Web Vitals in background (dev: console / prod: PostHog).
    initWebVitals();
    return () => {
      try {
        document.head.removeChild(link);
      } catch (e) {
        // Link may already be gone (StrictMode double-mount in dev). Don't crash.
        logger.debug("app:preload-cleanup", e);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/menu" element={<MenuPage />} />
                  {/* /locations was merged into /events in Feb 2026 — redirect for backward compat (saved bookmarks, old emails). */}
                  <Route path="/locations" element={<Navigate to="/events" replace />} />
                  <Route path="/rewards" element={<Rewards />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/order" element={<Order />} />
                  <Route path="/order/checkout" element={<OrderCheckout />} />
                  <Route path="/order/confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/staff/setup" element={<StaffSetup />} />
                  <Route path="/signup" element={<CustomerSignup />} />
                  <Route path="/menu-board" element={<MenuBoard />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/gift-cards" element={<GiftCard />} />

                  <Route path="/admin" element={<RequireStaff><AdminShell /></RequireStaff>}>
                    <Route index element={<Dashboard />} />
                    <Route path="pos" element={<POS />} />
                    <Route path="orders" element={<OrdersAdmin />} />
                    <Route path="menu" element={<MenuAdmin />} />
                    <Route
                      path="menu-board"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <MenuBoardEditor />
                        </RequireStaff>
                      }
                    />
                    <Route path="inventory" element={<InventoryAdmin />} />
                    <Route path="customers" element={<CustomersAdmin />} />
                    <Route path="reports" element={<ReportsAdmin />} />
                    <Route
                      path="settings"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <SettingsAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="payments"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <PaymentsAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <UsersAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="events"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <EventsAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="loyalty"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <LoyaltyAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="shifts"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <ShiftsAdmin />
                        </RequireStaff>
                      }
                    />
                    <Route
                      path="gift-cards"
                      element={
                        <RequireStaff roles={["owner", "manager"]}>
                          <GiftCardsAdmin />
                        </RequireStaff>
                      }
                    />
                  </Route>
                </Routes>
              </Suspense>
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </div>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
